// Copyright (C) AirWorks Solutions, Inc - All Rights Reserved
// DO NOT REDISTRIBUTE
// UNAUTHORIZED COPYING OF THIS FILE, ANY PART OR WHOLE, VIA ANY MEDIUM IS STRICTLY PROHIBITED
// PROPRIETARY AND CONFIDENTIAL

import { useState } from 'react';
import { Collapse, Typography, Chip, Divider, Select, MenuItem, IconButton } from '@mui/material';
import { Box, useTheme } from '@mui/system';
import type { SelectChangeEvent } from '@mui/material/Select';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import SettingsIcon from '@mui/icons-material/Settings';
import moment from 'moment';
import { useAppSelector } from 'Hooks';
import stylesDef from './styles';

interface OrderSectionProps {
  orders: IOrder[];
  selectedOrderId: string;
  onSelectOrder: (id: string) => void;
  activeOrderId: string | undefined;
  onViewLayers: () => void;
}

const OrderSection = ({ orders, selectedOrderId, onSelectOrder, activeOrderId, onViewLayers }: OrderSectionProps) => {
  const order = orders.find((o) => o._id === selectedOrderId) || orders[orders.length - 1];
  const orderIndex = orders.indexOf(order);
  const isActive = order._id === activeOrderId;
  const theme = useTheme();
  const styles = stylesDef(theme);

  const [expanded, setExpanded] = useState(true);

  const getStatusBadge = () => {
    if (order.orderStatus === 7) {
      return { dot: styles.completedDot, label: 'Completed' };
    }
    if (order.orderStatus > 0 && order.orderStatus < 7) {
      return { dot: styles.processingDot, label: 'Processing' };
    }
    if (order.isEstimate) {
      return { dot: styles.estimateDot, label: 'Estimate' };
    }
    return null;
  };

  const statusBadge = getStatusBadge();

  // Read bundle state as fallback for estimates without order-level data
  const selectedBundle = useAppSelector((state) => state.bundle.selectedBundle);
  const bundleDeliverables = useAppSelector((state) => state.bundle.bundleDeliverables);
  const selectedAdditionals = useAppSelector((state) => state.bundle.selectedAdditionals);

  // Always read from order first, fall back to bundle state for active estimates
  const productName = order.bundleName
    || (isActive && order.isEstimate ? selectedBundle?.name : null)
    || 'No bundle selected';

  // Combine order-level deliverables + addOns; fall back to bundle state for estimates
  const orderDeliverables = order.deliverables || [];
  const orderAddOns = order.addOns || [];
  const hasOrderLevelData = orderDeliverables.length > 0 || orderAddOns.length > 0;

  const layerCount = hasOrderLevelData
    ? orderDeliverables.length + orderAddOns.length
    : (isActive && order.isEstimate
      ? (bundleDeliverables?.length || 0) + (selectedAdditionals?.length || 0)
      : 0);

  // Build deliverable chips — prefer order data, fall back to bundle state
  const deliverableChips: { name: string; type: string }[] = [];
  if (hasOrderLevelData) {
    orderDeliverables.forEach((d) => {
      deliverableChips.push({ name: d.name, type: d.addOnType || d.type || '' });
    });
    orderAddOns.forEach((d) => {
      deliverableChips.push({ name: d.name, type: d.addOnType || d.type || '' });
    });
  } else if (isActive && order.isEstimate) {
    bundleDeliverables?.forEach((d) => {
      deliverableChips.push({ name: d.name, type: d.type });
    });
    selectedAdditionals?.forEach((d) => {
      deliverableChips.push({ name: d.name, type: d.type });
    });
  }

  // Show deliverable-type chips (file format add-ons)
  const formatChips = deliverableChips.filter((d) => d.type === 'deliverable');

  // Show order details only if the order has data or is an estimate
  const showOrderDetails = hasOrderLevelData || order.isEstimate;

  const formatDate = (date: string) => {
    if (!date) return 'N/A';
    return moment.utc(date).format('MMM D, YYYY');
  };

  return (
    <Box sx={styles.container}>
      <Box sx={styles.header} onClick={() => setExpanded(!expanded)}>
        <Typography variant="h5">ORDER DETAILS</Typography>
        <Box sx={styles.headerRight}>
          {expanded
            ? <KeyboardArrowUpIcon fontSize="small" />
            : <KeyboardArrowDownIcon fontSize="small" />}
        </Box>
      </Box>
      <Collapse in={expanded}>
        <Box sx={styles.content}>
          <Box sx={styles.orderSelectRow}>
            <Select
              value={selectedOrderId || order._id}
              onChange={(e: SelectChangeEvent) => onSelectOrder(e.target.value)}
              variant="standard"
              disableUnderline
              size="small"
              sx={styles.orderSelect}
            >
              {[...orders].reverse().map((o) => (
                <MenuItem key={o._id} value={o._id}>
                  Order {orders.indexOf(o) + 1}
                </MenuItem>
              ))}
            </Select>
            {statusBadge && (
              <Box sx={styles.statusBadge}>
                <Box sx={statusBadge.dot} />
                <Typography sx={styles.statusText}>{statusBadge.label}</Typography>
              </Box>
            )}
          </Box>
          {showOrderDetails && (
            <>
              {/* Dates */}
              <Box sx={styles.datesRow}>
                <Box>
                  <Typography sx={styles.dateLabel}>Order Date</Typography>
                  <Box sx={styles.dateValue}>
                    <CalendarTodayIcon sx={styles.dateIcon} />
                    <Typography variant="body2">{formatDate(order.orderDate)}</Typography>
                  </Box>
                </Box>
                <Box>
                  <Typography sx={styles.dateLabel}>Anticipated Kickoff</Typography>
                  <Box sx={styles.dateValueHighlight}>
                    <CalendarTodayIcon sx={styles.dateIconHighlight} />
                    <Typography variant="body2" sx={styles.dateTextHighlight}>{formatDate(order.anticipatedKickoffDate)}</Typography>
                  </Box>
                </Box>
              </Box>

              {/* Product */}
              <Typography sx={styles.sectionLabel}>Product</Typography>
              <Box sx={styles.productRow}>
                <SettingsIcon sx={styles.productIcon} />
                <Box>
                  <Typography sx={styles.productName}>{productName}</Typography>
                  <Typography component="span" sx={styles.layerCount}>
                    {`${layerCount} layers included. `}
                  </Typography>
                  <Typography
                    component="span"
                    sx={styles.layerLink}
                    onClick={onViewLayers}
                  >
                    View Layers
                  </Typography>
                </Box>
              </Box>

              {/* Deliverables */}
              {formatChips.length > 0 && (
                <>
                  <Typography sx={styles.sectionLabel}>Deliverables</Typography>
                  <Box sx={styles.chipsRow}>
                    {formatChips.map((d) => (
                      <Chip
                        key={d.name}
                        label={d.name}
                        variant="outlined"
                        size="small"
                        sx={styles.chip}
                      />
                    ))}
                  </Box>
                </>
              )}
            </>
          )}

        </Box>
      </Collapse>
    </Box>
  );
};

export default OrderSection;
